import React, { useEffect, useRef, useState } from 'react';
import { Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Search } from 'lucide-react';
import { companyService, userService, setAuthToken } from '../services/apiService';
import { planKey, planLabel } from './PlanFilter';

const MIN_LENGTH = 2;
const PREVIEW_COUNT = 5;

const plural = (count, word) => `${count.toLocaleString()} ${word}${count === 1 ? '' : 's'}`;

const ResultGroup = ({ title, items, total, seeAllTo, noun, renderItem }) => (
  <Col md={6}>
    <h6 className="mb-2">{title}</h6>
    {items.length === 0 ? (
      <p className="text-muted mb-0">No {noun}s match</p>
    ) : (
      <>
        <div className="home-search-results">{items.map(renderItem)}</div>
        {total > items.length && (
          <Link to={seeAllTo} className="d-inline-block mt-2">See all {plural(total, noun)}</Link>
        )}
      </>
    )}
  </Col>
);

/** One box that finds companies and users together; the first five of each show here, the rest on their own pages. */
const HomeSearch = () => {
  const { instance, accounts } = useMsal();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  // The query currently in the box, so a slow older search cannot overwrite newer results
  const latestRef = useRef('');

  // "/" jumps to the search box, unless the key is being typed into a field
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const { tagName, isContentEditable } = e.target;
      if (isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName)) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(timerRef.current);
    };
  }, []);

  const runSearch = async (query) => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ['api://7e9ba977-33b0-4e9c-ae75-55f65da317bd/access'],
        account: accounts[0],
      });
      setAuthToken(tokenResponse.accessToken);
      const [companies, users] = await Promise.all([
        companyService.searchCompanies(query),
        userService.searchUsers(query),
      ]);
      if (latestRef.current !== query) return;
      setResults({ query, companies: companies.data, users: users.data });
      setError(null);
    } catch (err) {
      if (latestRef.current !== query) return;
      console.error('Failed to search companies and users', err);
      setError('Failed to search. Please try again.');
    } finally {
      if (latestRef.current === query) setLoading(false);
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setTerm(value);
    clearTimeout(timerRef.current);
    const query = value.trim();
    latestRef.current = query;

    if (query.length < MIN_LENGTH) {
      setResults(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    timerRef.current = setTimeout(() => runSearch(query), 300);
  };

  const showPanel = term.trim().length >= MIN_LENGTH;
  const encoded = results ? encodeURIComponent(results.query) : '';

  return (
    <Card className="shadow-sm mb-4 home-search-card">
      <Card.Header className="bg-light">
        <div className="home-search">
          <Search size={16} className="home-search-icon" aria-hidden="true" />
          <Form.Control
            ref={inputRef}
            type="search"
            placeholder="Search companies and users"
            aria-label="Search companies and users"
            value={term}
            onChange={handleChange}
          />
          {/* Hidden once there is text, where the browser puts its clear button */}
          {!term && <kbd className="home-search-hint d-none d-md-block" aria-hidden="true">/</kbd>}
        </div>
      </Card.Header>
      {showPanel && (
        <Card.Body>
          {error ? (
            <p className="text-danger mb-0">{error}</p>
          ) : loading || !results ? (
            <div className="text-muted"><Spinner animation="border" size="sm" className="me-2" />Searching...</div>
          ) : (
            <Row className="g-4">
              <ResultGroup
                title="Companies"
                noun="company"
                items={results.companies.slice(0, PREVIEW_COUNT)}
                total={results.companies.length}
                seeAllTo={`/companies?q=${encoded}`}
                renderItem={(company) => (
                  <Link key={company.id} to={`/companies/${company.id}`} className="home-search-item">
                    <span className="home-search-item-title">{company.name}</span>
                    <span className="home-search-item-meta">
                      {planLabel(planKey(company))} · {plural(company.totalUsers || 0, 'user')}
                      {!company.enabled && ' · disabled'}
                    </span>
                  </Link>
                )}
              />
              <ResultGroup
                title="Users"
                noun="user"
                items={results.users.slice(0, PREVIEW_COUNT)}
                total={results.users.length}
                seeAllTo={`/user-search?q=${encoded}`}
                renderItem={(user) => (
                  <Link key={user.id} to={`/users/${user.id}`} className="home-search-item">
                    <span className="home-search-item-title">
                      {[user.firstname, user.lastname].filter(Boolean).join(' ') || user.email}
                    </span>
                    <span className="home-search-item-meta">
                      {[user.email, user.companyName].filter(Boolean).join(' · ')}
                    </span>
                  </Link>
                )}
              />
            </Row>
          )}
        </Card.Body>
      )}
    </Card>
  );
};

export default HomeSearch;
